import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from './components/LoadingSpinner';
import ServiceDashboard from './ServiceDashboard';
import NavigatorScreen from './NavigatorScreen';
import type { StaffRolesMap } from './context/StaffRolesContext';

const EventsService = lazy(() => import('../services/events/miniapp/EventsRoot'));
const EventsManager = lazy(() => import('../services/events/miniapp/EventsManagerRoot'));
const DigitalMediaService = lazy(() => import('../services/digital-media/miniapp/DigitalMediaRoot'));

interface AppRoutesProps {
  activeServices: string[];
  staffRoles: StaffRolesMap;
  servicesLoading: boolean;
  servicesError: string | null;
}

interface ServiceRouteGateProps {
  serviceName: string;
  activeServices: string[];
  servicesLoading: boolean;
  servicesError: string | null;
  children: ReactNode;
}

function ServiceRouteGate({
  serviceName,
  activeServices,
  servicesLoading,
  servicesError,
  children,
}: ServiceRouteGateProps) {
  const { t } = useTranslation('shell');

  if (servicesLoading) {
    return <LoadingSpinner />;
  }

  if (servicesError) {
    return <p className="p-6 text-error">{servicesError}</p>;
  }

  if (!activeServices.includes(serviceName)) {
    return <p className="p-6 text-text-secondary">{t('service.unavailable')}</p>;
  }

  return <>{children}</>;
}

export function AppRoutes({ activeServices, staffRoles, servicesLoading, servicesError }: AppRoutesProps) {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        <Route
          path="/"
          element={
            <ServiceDashboard
              activeServices={activeServices}
              staffRoles={staffRoles}
              servicesLoading={servicesLoading}
              servicesError={servicesError}
            />
          }
        />
        <Route path="/navigator/" element={<NavigatorScreen />} />
        <Route
          path="/digital-media/*"
          element={
            <ServiceRouteGate
              serviceName="digital-media"
              activeServices={activeServices}
              servicesLoading={servicesLoading}
              servicesError={servicesError}
            >
              <DigitalMediaService />
            </ServiceRouteGate>
          }
        />
        <Route
          path="/events/manager/*"
          element={
            <ServiceRouteGate
              serviceName="events"
              activeServices={activeServices}
              servicesLoading={servicesLoading}
              servicesError={servicesError}
            >
              <EventsManager />
            </ServiceRouteGate>
          }
        />
        <Route
          path="/events/*"
          element={
            <ServiceRouteGate
              serviceName="events"
              activeServices={activeServices}
              servicesLoading={servicesLoading}
              servicesError={servicesError}
            >
              <EventsService />
            </ServiceRouteGate>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
